/**
 * AnalyticsCategoryList Component
 * 
 * Category/account breakdown list with placeholder support.
 */

import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  useMediaQuery,
  useTheme,
  IconButton,
  Divider,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Account } from "../../../types";
import { CategoryData } from "./AnalyticsPieChart";

// Note: Filter icon opens Activity page with filters, category name opens graph detail page

export interface AnalyticsCategoryListProps {
  selectedTab: "expenses" | "incomes" | "adjustments";
  isShowingPlaceholder: boolean;
  categoryData: CategoryData[];
  totalAmount: number;
  formatAmount: (amount: number) => string;
  getCategoryColor: (index: number) => string;
  placeholderItems: string[];
  accounts: Account[];
  selectedAccounts: string[];
  selectedUsers: string[];
  workspaceMembersLength: number;
  fromDate: string;
  toDate: string;
  selectedPeriod?: string;
  onNavigateToActivity?: (filters?: {
    category?: string;
    account?: string;
    users?: string[];
    startDate?: string;
    endDate?: string;
    typeFilter?: string;
  }) => void;
  onNavigateToDetail?: (params: {
    name: string;
    type: "category" | "group" | "account";
    selectedTab: "expenses" | "incomes" | "adjustments";
    filters?: {
      category?: string;
      account?: string;
      users?: string[];
      startDate?: string;
      endDate?: string;
      typeFilter?: string;
    };
  }) => void;
  aggregationMode?: "category" | "group";
}

/**
 * Category breakdown list component
 */
export const AnalyticsCategoryList: React.FC<AnalyticsCategoryListProps> = ({
  selectedTab,
  isShowingPlaceholder,
  categoryData,
  totalAmount,
  formatAmount,
  getCategoryColor,
  placeholderItems,
  accounts,
  selectedAccounts,
  selectedUsers,
  workspaceMembersLength,
  fromDate,
  toDate,
  selectedPeriod,
  onNavigateToActivity,
  onNavigateToDetail,
  aggregationMode = "category",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  
  return (
    <Box sx={{ 
      flex: "1 1 40%", 
      display: "flex", 
      flexDirection: "column", 
      borderLeft: isMobile ? 0 : 1, 
      borderColor: "divider", 
      pl: isMobile ? 0 : 3 
    }}>
      <Stack spacing={0} sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
        {isShowingPlaceholder ? (
          // Show category/account list placeholder with percentage placeholders
          placeholderItems.length > 0 ? (
            placeholderItems.map((itemName, index) => (
              <React.Fragment key={itemName}>
                {index > 0 && <Divider sx={{ my: 0.25 }} />}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.5, userSelect: "none" }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "2px",
                      bgcolor: getCategoryColor(index),
                      flexShrink: 0,
                      opacity: 0.3,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "text.secondary",
                        textAlign: "left",
                        userSelect: "none",
                      }}
                    >
                      {itemName}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right", minWidth: 60 }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ opacity: 0.5, userSelect: "none" }}>
                      -
                    </Typography>
                  </Box>
                </Box>
              </React.Fragment>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No data available
            </Typography>
          )
        ) : (
          categoryData.map((item, index) => {
            const handleCategoryClick = (): void => {
              if (!onNavigateToActivity) return;
              
              // Prepare filters based on current Analytics state
              const filters: {
                category?: string;
                account?: string;
                users?: string[];
                startDate?: string;
                endDate?: string;
                typeFilter?: string;
              } = {};
              
              // Set category (only for expenses/incomes, not adjustments, and only when in category mode)
              // When in group mode, don't set category filter since item.name is a group name, not a category
              if ((selectedTab === "expenses" || selectedTab === "incomes") && aggregationMode === "category") {
                filters.category = item.name;
                filters.typeFilter = selectedTab === "expenses" ? "Expenses" : "Income";
              } else if ((selectedTab === "expenses" || selectedTab === "incomes") && aggregationMode === "group") {
                // In group mode, only set type filter (no category filter since we're showing groups)
                filters.typeFilter = selectedTab === "expenses" ? "Expenses" : "Income";
              }
              
              // For adjustments, set type filter and account filter
              if (selectedTab === "adjustments") {
                filters.typeFilter = "Adjustment";
                // Handle "Unknown Account" or deleted accounts (name contains "(Deleted)")
                if (item.name === "Unknown Account" || item.name.includes("(Deleted)")) {
                  // For unknown/deleted accounts, we can't easily filter in Activity
                  // because Activity filters by accountId, and these accounts don't exist
                  // So we'll just set the type filter and let Activity show all adjustments
                  // The user can manually identify which ones are from deleted accounts
                  // Note: This is a limitation - ideally Activity would support filtering
                  // by missing/invalid accountId, but that requires Activity page changes
                } else {
                  // Find account ID from account name (item.name is the account name for adjustments)
                  const account = accounts.find((acc) => acc.accountName === item.name);
                  if (account) {
                    filters.account = account.id;
                  }
                }
              }
              
              // Set account if one is selected (and not already set for adjustments)
              // Activity page currently supports single account
              if (selectedAccounts.length === 1 && !filters.account) {
                filters.account = selectedAccounts[0];
              }
              
              // Set users if any are selected
              if (selectedUsers.length > 0 && selectedUsers.length < workspaceMembersLength) {
                filters.users = selectedUsers;
              }
              
              // Set date range
              if (fromDate) filters.startDate = fromDate;
              if (toDate) filters.endDate = toDate;
              
              onNavigateToActivity(filters);
            };

            const handleDetailClick = (e?: React.MouseEvent): void => {
              e?.stopPropagation();
              
              // Determine the type based on selectedTab and aggregationMode
              let detailType: "category" | "group" | "account" = "category";
              if (selectedTab === "adjustments") {
                detailType = "account";
              } else if (aggregationMode === "group") {
                detailType = "group";
              }

              // Navigate to detail page
              if (workspaceSlug) {
                const nameParam = encodeURIComponent(item.name);
                const params = new URLSearchParams();
                if (selectedPeriod) {
                  params.set("period", selectedPeriod);
                }
                if (fromDate) {
                  params.set("fromDate", fromDate);
                }
                if (toDate) {
                  params.set("toDate", toDate);
                }
                // Pass tab so detail page shows correct label/color (Expenses vs Income)
                if (selectedTab === "expenses" || selectedTab === "incomes") {
                  params.set("tab", selectedTab);
                }
                const queryString = params.toString();
                const url = `/${workspaceSlug}/analytics/${detailType}/${nameParam}${queryString ? `?${queryString}` : ""}`;
                navigate(url);
              } else if (onNavigateToDetail) {
                // Fallback to callback if workspaceSlug is not available
                const filters: {
                  category?: string;
                  account?: string;
                  users?: string[];
                  startDate?: string;
                  endDate?: string;
                  typeFilter?: string;
                } = {};
                
                if (selectedTab === "expenses" || selectedTab === "incomes") {
                  if (aggregationMode === "group") {
                    // For groups, we don't set category filter
                  } else {
                    filters.category = item.name;
                  }
                  filters.typeFilter = selectedTab === "expenses" ? "Expenses" : "Income";
                }
                
                if (selectedTab === "adjustments") {
                  filters.typeFilter = "Adjustment";
                  const account = accounts.find((acc) => acc.accountName === item.name);
                  if (account) {
                    filters.account = account.id;
                  }
                }
                
                if (selectedAccounts.length === 1 && !filters.account) {
                  filters.account = selectedAccounts[0];
                }
                
                if (selectedUsers.length > 0 && selectedUsers.length < workspaceMembersLength) {
                  filters.users = selectedUsers;
                }
                
                if (fromDate) filters.startDate = fromDate;
                if (toDate) filters.endDate = toDate;
                
                onNavigateToDetail({
                  name: item.name,
                  type: detailType,
                  selectedTab,
                  filters,
                });
              }
            };
            
            return (
              <React.Fragment key={item.name}>
                {index > 0 && <Divider sx={{ my: 0.25 }} />}
                <Box
                  onClick={handleDetailClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleDetailClick();
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 0.5,
                    width: "100%",
                    cursor: "pointer",
                    userSelect: "none",
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "2px",
                      bgcolor: item.color,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color="primary.main"
                      sx={{ userSelect: "none" }}
                    >
                      {item.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={selectedTab === "adjustments" && item.value < 0 ? "error.main" : "text.primary"}
                      sx={{ userSelect: "none", minWidth: 60, textAlign: "right" }}
                    >
                      {formatAmount(item.value)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryClick();
                      }}
                      sx={{
                        padding: 0.5,
                        color: "text.secondary",
                        "&:hover": { color: "primary.main", bgcolor: "action.hover" },
                      }}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </React.Fragment>
            );
          })
        )}
      </Stack>
    </Box>
  );
};
